import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Draggable from 'react-draggable';
import { v4 as uuidv4 } from 'uuid'; // Import UUID library
import './styles.css';
import defaultStickies from './stickies';
import setUpBoard from './setUpBoard';

export default function App() {
  const apiUrl = window.env.API_URL;
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [boardId, setBoardId] = useState(() => {
    return localStorage.getItem('boardId') || uuidv4();
  });
  const [stickyNotes, setStickyNotes] = useState(defaultStickies);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [history, setHistory] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedState, setSelectedState] = useState([]);

  const previousStickyNotesRef = useRef(stickyNotes);

  useEffect(() => {
    localStorage.setItem('boardId', boardId);

    const fullAuthUrl = `${apiUrl}/is-authenticated`;
    axios
      .get(fullAuthUrl)
      .then((response) => {
        console.log('Received from auth call:', response.data);
        const responseData = response.data;
        let checkedIsAuthenticated = responseData.isAuthenticated;
        checkedIsAuthenticated =
          checkedIsAuthenticated &&
          (responseData.role === 'user' || responseData.role === 'admin');
        setIsAuthenticated(checkedIsAuthenticated);
      })
      .catch((error) => {
        console.log('Error getting auth', error);
        setIsAuthenticated(false);
      });
  }, [apiUrl, boardId]); // Re-run when apiUrl or boardId changes
  const allSquares = setUpBoard(); 
  useEffect(() => {
    const loadBoard = async () => {
      try {
        const response = await fetch(`${apiUrl}/load/${boardId}`);
        if (response.ok) {
          const data = await response.json();
          console.log('Loaded board state:', data);
          setStickyNotes(data);
        } else {
          console.warn('Failed to load board state.');
        }
      } catch (error) {
        console.error('Error loading board state:', error);
      }
    };

    loadBoard();
  }, [apiUrl, boardId]); // Runs once when the component mounts

  useEffect(() => {
    const intervalId = setInterval(async () => {
      const currentState = stickyNotes;
      const previousState = previousStickyNotesRef.current;

      if (JSON.stringify(currentState) !== JSON.stringify(previousState)) {
        previousStickyNotesRef.current = currentState; // Update the ref to the current state

        const boardState = stickyNotes.map((note) => ({
          id: note.id,
          x: note.x,
          y: note.y,
          color: note.color,
          text: note.text,
        }));

        try {
          await fetch(`${apiUrl}/save-board`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ boardId, state: boardState }),
          });
          console.log('Board state saved automatically.');
        } catch (error) {
          console.error('Error saving board state:', error);
        }
      }
    }, 5000); // Save every 5 seconds

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [apiUrl, boardId, stickyNotes]); // Re-run if stickyNotes changes

  const handleDragStart = (e, data, id) => {
    setActiveNoteId(id);
  };

  const handleDragStop = (e, data, id) => {
    setActiveNoteId(null);
    setStickyNotes((prevNotes) =>
      prevNotes.map((note) =>
        note.id === id ? { ...note, x: data.x, y: data.y } : note
      )
    );
  };

  const handleReset = () => {
    setStickyNotes(defaultStickies);
  };

  const handleLoad = async () => {
    try {
      const response = await fetch(`${apiUrl}/load/${boardId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded board state:', data);
        setStickyNotes(data);
        alert('Board state loaded successfully!');
      } else {
        alert('Failed to load board state.');
      }
    } catch (error) {
      console.error('Error loading board state:', error);
      alert('Error loading board state. Check console for details.');
    }
  };

  const handleSave = async () => {
    const boardState = stickyNotes.map((note) => ({
      id: note.id,
      x: note.x,
      y: note.y,
      color: note.color,
      text: note.text,
    }));

    try {
      const response = await fetch(`${apiUrl}/save-board`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ boardId, state: boardState }),
      });

      if (response.ok) {
        alert('Board state saved successfully!');
      } else {
        alert('Failed to save board state.');
      }
    } catch (error) {
      console.error('Error saving board state:', error);
      alert('Error saving board state. Check console for details.');
    }
  };

  const handleNew = () => {
    const newSticky = prompt("What's the chore?", 'New Chore');
    if (!newSticky) return;
    const newNote = {
      id: stickyNotes.length + 1,
      x: 32 * 5,
      y: 32 * 21,
      color: 'yellow',
      text: newSticky,
    };
    setStickyNotes((prevNotes) => [...prevNotes, newNote]);
  };


  const handleHistory = async () => {
    try {
      const response = await axios.get(`${apiUrl}/history`);
      setHistory(response.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleNewBoard = async () => {
    const newBoardId = uuidv4();
    setBoardId(newBoardId);
    localStorage.setItem('boardId', newBoardId);
    handleReset();
  };

  const handleSelectBoard = () => {
    if (selectedState.length > 0) {
      setStickyNotes(selectedState);
      setIsModalOpen(false);
    }
  };

  return (
    <div className="app">
      <div className="board">
        {allSquares.map((square, index) => (
          <div
            className="square"
            key={index}
            style={{
              top: square.y + 'px',
              left: square.x + 'px',
            }}
          />
        ))}

        {stickyNotes.map((note) => (
          <Draggable
            key={note.id}
            position={{ x: note.x, y: note.y }}
            grid={[32, 32]}
            onStart={(e, data) => handleDragStart(e, data, note.id)}
            onStop={(e, data) => handleDragStop(e, data, note.id)}
          >
            <div
              className="note"
              style={{
                backgroundColor: note.color,
                zIndex: activeNoteId === note.id ? 1000 : 1,
              }}
            >
              {note.text}
            </div>
          </Draggable>
        ))}
      </div>
      {isAuthenticated && (
        <>
          <button className="saveButton" onClick={handleSave}>
            Save
          </button>
          <button className="loadButton" onClick={handleLoad}>
            Load
          </button>
          <button className="resetButton" onClick={handleReset}>
            Reset
          </button>
          <button className="newButton" onClick={handleNew}>
            Sticky+
          </button>
          <button className="historyButton" onClick={handleHistory}>
            History
          </button>
          <button className="newBoardButton" onClick={handleNewBoard}>
            Board+
          </button>
        </>
      )}
      {isModalOpen && (
        <div className="modal">
          <div className="modalContent">
            <h2>Board History</h2>
            <ul>
              {history.map(entry => (
                <li key={entry.board_id}>
                  <label>
                    <input type="radio" name="history" onChange={() => setSelectedState(entry.state)} />
                    {new Date(entry.updated_at).toLocaleString()}
                  </label>
                </li>
              ))}
            </ul>
            <button onClick={handleSelectBoard}>OK</button>
            <button onClick={() => setIsModalOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
